import { useEffect, useState } from "react"

import { UploadOutlined } from "@ant-design/icons"
import { Avatar, Col, Drawer, Flex, Form, Modal, Row, Space, Typography, Upload } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import ButtonComponent from "../../../Components/Button"
import InputComponent from "../../../Components/Input"
import withPermission from "../../../hoc/withPermission"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { useIncognito } from "../../../hooks/useIncognito"
import Icon from "../../../Icon"
import { highAccessRoles } from "../../../Utils/permission"
import { validateUrl } from "../../../Utils/utils"

import "./EditBussinessAccountModal.less"

const { Text } = Typography

const PermissionButtonHigh = withPermission(ButtonComponent, highAccessRoles)
const PermissionInputHigh = withPermission(InputComponent, highAccessRoles)

const fakeRequest = ({ onSuccess }) => {
  setTimeout(() => {
    onSuccess("ok")
  }, 500)
}

const EditBusinessAccountModal = ({
  open,
  closeModal,
  businessAccount,
  businessAccountId,
  saveFetching,
  ba_role,
  updateBusinessAccount
}) => {
  const { t } = useTranslation()
  const externalDemoMode = useExternalDemoMode()
  const incognito = useIncognito()
  const isMobile = useSelector((state) => state.nav.isMobile)

  const { name, url, photo } = businessAccount || {
    name: null,
    url: null,
    photo: null,
    is_disabled_auto_cancel: false,
    enable_auto_cancel: false,
    auto_cancel_amount: null
  }
  console.log(businessAccount?.photo)
  const [avatar, setAvatar] = useState(null)
  const [avatarUrl, setAvatarUrl] = useState(photo)
  const [avatarError, setAvatarError] = useState("")
  const [hasAvatarChanged, setHasAvatarChanged] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [isValid, setIsValid] = useState(true)
  const [revealed, setRevealed] = useState({})
  const formName = Form.useWatch("name", form)

  useEffect(() => {
    setAvatar(photo)
    setAvatarUrl(photo)
    setAvatarError("")
    setHasAvatarChanged(false)
    setRevealed({})
    form.setFieldsValue({
      name,
      url
    })
    updateIsValid()
  }, [businessAccount, businessAccountId])

  const updateIsValid = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
    setIsValid(!hasErrors)
  }

  const onFieldsChange = () => {
    updateIsValid()
  }

  const onFinish = (values) => {
    updateBusinessAccount({
      id: businessAccountId,
      ...values,
      ...(hasAvatarChanged ? { photo: avatar } : {})
    })
    closeModal()
  }

  const businessAccountRole = ba_role

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png"
    if (!isJpgOrPng) {
      setAvatarError(
        "You can upload file only in JPG/ PNG formats, please select a different image."
      )
      setLoading(false)
      return false
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      setAvatarError(
        "Image upload failed: your image is bigger than 10MB, please select a different image."
      )
      setLoading(false)
      return false
    }
    if (avatarError) setAvatarError("")
    return true
  }

  const handleChangeImage = (info = null) => {
    if (info === null) {
      if (avatarError) setAvatarError("")
      setLoading(false)
      setAvatar(null)
      setAvatarUrl(null)
      setHasAvatarChanged(true)
      return
    }
    if (info.file.status === "uploading") {
      setLoading(true)
      return
    }
    if (info.file.status === "done") {
      setAvatar(info.file.originFileObj)
      setAvatarUrl(URL.createObjectURL(info.file.originFileObj))
      setHasAvatarChanged(true)
      setLoading(false)
    }
  }

  const formContent = (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={{ name, url }}
      className="with-default-error"
      onFieldsChange={onFieldsChange}
    >
      <Row className="edit-business-acc_avatar-upload-row">
        <Col flex={"0 1 420px"} className="edit-business-acc_file-label">
          <Text strong>
            {t("Business logotype")}
            <Text type="secondary">{` (${t("optional")})`}</Text>
          </Text>
        </Col>
        <Col flex={"0 1 420px"}>
          <Space size={16}>
            <Avatar
              className="user_avatar"
              size={64}
              style={{
                backgroundColor: avatarUrl ? "transparent" : "#225AEA",
                borderRadius: "12px",
                border: "none"
              }}
              {...(avatarUrl && { src: avatarUrl })}
            >
              {!avatarUrl ? formName?.[0] || "" : null}
            </Avatar>

            <Space size={12} direction="vertical">
              <Space size={8}>
                <Upload
                  name="media"
                  accept=".jpeg,.jpg,.png"
                  className="upload-avatar"
                  showUploadList={false}
                  customRequest={fakeRequest}
                  beforeUpload={beforeUpload}
                  onChange={handleChangeImage}
                  disabled={externalDemoMode}
                >
                  <PermissionButtonHigh
                    icon={<UploadOutlined />}
                    loading={loading || saveFetching}
                    disabled={loading || saveFetching || externalDemoMode}
                    className="flex"
                    role={businessAccountRole}
                  >
                    {t(loading ? "Uploading" : photo ? "Replace image" : "Click to Upload")}
                  </PermissionButtonHigh>
                </Upload>

                <PermissionButtonHigh
                  disabled={!photo && !avatarUrl}
                  onClick={() => handleChangeImage()}
                  role={businessAccountRole}
                >
                  {t("Remove")}
                </PermissionButtonHigh>
              </Space>

              <Space size={8} className="error">
                {avatarError && <Icon type={"alert_triangle_error"} style={{ fontSize: 16 }} />}
                <Text
                  type={avatarError ? "danger" : "secondary"}
                  style={{ fontSize: 12, lineHeight: 1.5 }}
                >
                  {avatarError
                    ? t(avatarError)
                    : "Upload *.png or *.jpeg files, up to 10MB, recommended size is 400x400px."}
                </Text>
              </Space>
            </Space>
          </Space>
        </Col>
      </Row>

      <Row className="edit-business-acc_business-name-row">
        <Col flex={"0 1 420px"} className="edit-business-acc_input-label">
          <Space size={4}>
            <Text strong>{t("Business name")}</Text>
            <Text strong className="edit-business-acc_required">
              {"*"}
            </Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          {businessAccount?.id && (
            <Flex
              gap={12}
              align={"center"}
              style={{ height: "100%" }}
              className="edit-business-acc_form-item"
            >
              <Form.Item
                name="name"
                validateTrigger="onChange"
                rules={[
                  {
                    required: true,
                    message: "Please enter business name."
                  }
                ]}
              >
                <PermissionInputHigh
                  name="name"
                  role={businessAccountRole}
                  type={incognito && !revealed.name ? "password" : "text"}
                  onChange={() => setRevealed((prev) => ({ ...prev, name: true }))}
                />
              </Form.Item>
            </Flex>
          )}
        </Col>
      </Row>

      <Row>
        <Col flex={"0 1 420px"} className="edit-business-acc_input-label">
          <Space size={4}>
            <Text strong>{t("Business URL")}</Text>
            <Text strong className="edit-business-acc_required">
              {"*"}
            </Text>
          </Space>
        </Col>

        <Col flex={"0 1 420px"}>
          {businessAccount?.id && (
            <Flex
              gap={4}
              align={"start"}
              justify={"center"}
              vertical
              style={{ height: "100%" }}
              className="edit-business-acc_form-item"
            >
              <Form.Item
                name="url"
                rules={[
                  {
                    required: true,
                    message: t("Please enter valid business URL.")
                  },
                  {
                    pattern: validateUrl,
                    message: t("Please enter valid business URL.")
                  }
                ]}
                validateTrigger="onChange"
              >
                <PermissionInputHigh
                  name="url"
                  role={businessAccountRole}
                  type={incognito && !revealed.url ? "password" : "text"}
                  onChange={() => setRevealed((prev) => ({ ...prev, url: true }))}
                />
              </Form.Item>
            </Flex>
          )}
        </Col>
      </Row>

      {!isMobile && (
        <Flex className="edit-business-acc_btns" justify="flex-end" align="center" gap={12}>
          <ButtonComponent size="large" text={t("Cancel")} onClick={closeModal} />
          <ButtonComponent
            size="large"
            text={t("Confirm")}
            type="primary"
            htmlType="submit"
            disabled={!isValid || externalDemoMode}
          />
        </Flex>
      )}
    </Form>
  )

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={closeModal}
        placement="bottom"
        height="auto"
        className="edit-business-acc-drawer"
        title={t("Business information")}
        closeIcon={<Icon type="close_modal" />}
        styles={{
          body: { padding: 16, overflowY: "auto", maxHeight: "75vh" },
          header: {
            padding: "12px 16px",
            borderBottom: "1px solid var(--neutral-200)"
          },
          footer: {
            padding: "12px 16px",
            borderTop: "1px solid var(--neutral-200)"
          }
        }}
        footer={
          <Flex vertical gap={12}>
            <ButtonComponent
              size="large"
              style={{ width: "100%" }}
              text={t("Cancel")}
              onClick={closeModal}
            />
            <ButtonComponent
              size="large"
              style={{ width: "100%" }}
              text={t("Confirm")}
              type="primary"
              disabled={!isValid || externalDemoMode}
              onClick={() => form.submit()}
            />
          </Flex>
        }
      >
        <Text type="secondary">{t("Update your business information.")}</Text>
        {formContent}
      </Drawer>
    )
  }

  return (
    <Modal
      classNames={{ content: "clear-bg" }}
      open={open}
      title={t("Business information")}
      closeIcon={<Icon type="close_modal" />}
      footer={null}
      closable="true"
      onCancel={closeModal}
      width={450}
      className="bussiness-information-modal"
      destroyOnClose
    >
      <Text type="secondary" strong>
        {t("Update your business information.")}
      </Text>
      {formContent}
    </Modal>
  )
}

export default EditBusinessAccountModal
